'use client'

import Link from 'next/link'
import { MarketingLayout } from '@/components/marketing/MarketingLayout'

export default function HowItWorksPage() {
  return (
    <MarketingLayout>
      <section className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="eyebrow">Methodology</div>
            <h1>How Community Intelligence Works</h1>
            <p>Three layers that reveal the decision architecture of place—and predict how communities will respond.</p>
          </div>
        </div>
      </section>

      <section className="formula-section">
        <div className="container">
          <div className="formula-card">
            <p className="formula">Cohesion <span className="equals">=</span> Structure <span className="multiply">×</span> Alignment <span className="multiply">×</span> Trust </p>
          </div>
        </div>
      </section>

      <section className="framework-section">
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow">The Framework</div>
            <h2>Three layers of community intelligence</h2>
            <p>Three layers that reveal the decision architecture of a place at a point in time—and predict how communities will respond. The cohesion of any place is measured by its Structure, Alignment, and Trust.</p>
          </div>
          <div className="framework-layers">
            <div className="layer-card">
              <div className="layer-meta">
                <div className="layer-number">01</div>
                <span className="layer-status">Live</span>
              </div>
              <div className="layer-content">
                <h3>Structure</h3>
                <p>The environmental reality. What resources and systems exist to deliver benefits to a community. Simply put, these are the rules of the game.</p>
                <div className="layer-details">
                  <div className="layer-detail">
                    <h4>What we measure</h4>
                    <p>Nine civic pillars: Safety, Governance, Environment, Education, Infrastructure, Health, Economy, Social Capital, and Housing.</p>
                  </div>
                  <div className="layer-detail">
                    <h4>What it reveals</h4>
                    <p>Whether a community will embrace change, resist it, or fragment under pressure. Who you need to reach first—and how.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="layer-card">
              <div className="layer-meta">
                <div className="layer-number">02</div>
                <span className="layer-status">Live</span>
              </div>
              <div className="layer-content">
                <h3>Alignment</h3>
                <p>How people&apos;s behaviors receive what structure supplies. These patterns determine any response to change.</p>
                <div className="layer-details">
                  <div className="layer-detail">
                    <h4>Three behavioral patterns</h4>
                    <p><strong>Guardians</strong> protect stability. <strong>Expanders</strong> push for openness. <strong>Bridge Builders</strong> connect across differences.</p>
                  </div>
                  <div className="layer-detail">
                    <h4>What it reveals</h4>
                    <p>Whether a community will embrace the structure, resist it, or fragment under pressure. Who you need to reach first—and how.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="layer-card">
              <div className="layer-meta">
                <div className="layer-number">03</div>
                <span className="layer-status">Live</span>
              </div>
              <div className="layer-content">
                <h3>Trust Flow</h3>
                <p>Trust is made of behaviors—observable, measurable, and impactful. We detect it with our hyper-local network of behaviors.</p>
                <div className="layer-details">
                  <div className="layer-detail">
                    <h4>Two types of trust</h4>
                    <p><strong>Inward-facing trust</strong> uses bonding trust to move through the community. <strong>Outward-facing trust</strong> relies on bridging trust across communities.</p>
                  </div>
                  <div className="layer-detail">
                    <h4>What it reveals</h4>
                    <p>From the ratio of inward and outward behaviors, we can predict future behavior—and find, activate, and facilitate the change agents who drive it. Trust is the prerequisite to any change.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="clarify-section">
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow">Common Questions</div>
            <h2>What Community Intelligence is—and isn&apos;t</h2>
          </div>
          <div className="clarify-grid">
            <div className="clarify-card">
              <p className="question">&quot;We already do community engagement.&quot;</p>
              <h3>CI doesn&apos;t replace engagement—it focuses it.</h3>
              <p>Town halls tell you what people say. CI tells you how they&apos;ll actually behave. Use it to know where engagement will land.</p>
            </div>
            <div className="clarify-card">
              <p className="question">&quot;This sounds like surveillance.&quot;</p>
              <h3>We&apos;re blind by design.</h3>
              <p>No PII. No demographics. No tracking. We analyze behavioral patterns at the neighborhood level—never individuals.</p>
            </div>
            <div className="clarify-card">
              <p className="question">&quot;We&apos;re drowning in data already.&quot;</p>
              <h3>We give you decisions, not dashboards.</h3>
              <p>CI surfaces the 3-5 insights that matter for your specific situation. Clarity, not volume.</p>
            </div>
            <div className="clarify-card">
              <p className="question">&quot;Where are your case studies?&quot;</p>
              <h3>We&apos;re building proof points with early partners.</h3>
              <p>If you want early access to the methodology—and help prove it works—we should talk.</p>
            </div>
            <div className="clarify-card">
              <p className="question">&quot;Does this replace my team&apos;s expertise?&quot;</p>
              <h3>It amplifies it.</h3>
              <p>Your team knows how to engage communities. CI tells them where to focus—so they spend time on strategy, not guesswork.</p>
            </div>
            <div className="clarify-card">
              <p className="question">&quot;What do I actually get?&quot;</p>
              <h3>A clear picture of behavioral reality.</h3>
              <p>A community assessment, a strategic brief, or ongoing intelligence. Actionable insight, not a data dump.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="deliverables-section">
        <div className="container">
          <div className="section-header">
            <div className="section-eyebrow">Deliverables</div>
            <h2>Sample of what you receive</h2>
          </div>
          <div className="deliverables-grid">
            <div className="deliverable-card">
              <div className="icon">📊</div>
              <h3>Community Assessment</h3>
              <p>A deep-dive into a specific geography. Neighborhood-level maps, alignment scoring, trust flow analysis, and 5 strategic recommendations.</p>
            </div>
            <div className="deliverable-card">
              <div className="icon">🎯</div>
              <h3>Strategic Brief</h3>
              <p>A focused document for a specific initiative. Where to engage, who to activate, what resistance to expect, and how to sequence your approach.</p>
            </div>
            <div className="deliverable-card">
              <div className="icon">📡</div>
              <h3>Ongoing Intelligence</h3>
              <p>Updates on community dynamics. Early warning when conditions shift, with specific recommendations for adapting your strategy, and more.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2>See what CI reveals for your situation.</h2>
            <p>Tell us the community and the challenge. We&apos;ll show you what we can see.</p>
            <Link href="/use-cases" className="btn-primary">
              Explore Use Cases
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
                      </div>
        </div>
      </section>
    </MarketingLayout>
  )
}
